import React, { Fragment, useState, useEffect } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Card, Grid, Icon, Image, Label } from 'semantic-ui-react';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import Svg from 'react-inlinesvg';
import { cameraIcon, docOutlineIcon } from '../../../images';
import { Formatters } from '../../../utils';
import StyledImagesField from './ImagesField.style';

// extensions of images that can be previewable with blob URL
const imageExt = ['jpg', 'jpeg', 'gif', 'png', 'svg'];
const ImagesField = ({
  name,
  label,
  error,
  onChange,
  value,
  limitExt = ['jpg', 'png', 'pdf'],
  qa
}) => {
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState([]);
  const onDrop = acceptedFiles => {
    let dropErrors = [];
    let typeError = false;
    let updated = [...files, ...acceptedFiles];

    if (updated.length > 5) {
      dropErrors.push(
        <FormattedMessage
          id="error.not_supported"
          defaultMessage="Our current system only supports {limit}."
          values={{
            limit: '5 files'
          }}
        />
      );
    }
    // preview url won't work for IE11
    updated = updated
      .map(file =>
        Object.assign(file, {
          preview: URL && URL.createObjectURL(file),
          info: Formatters.getFileNameExt(file.name)
        })
      )
      .filter(file => {
        const isValid = limitExt.includes(
          file.info && file.info[1].toLowerCase()
        );
        if (!isValid) {
          typeError = true;
        }
        return isValid;
      })
      .slice(0, 5);

    if (typeError) {
      dropErrors.push(
        <FormattedMessage
          id="error.not_supported"
          defaultMessage="Our current system only supports {limit}."
          values={{
            limit: 'files in jpg, png and pdf format'
          }}
        />
      );
    }
    setErrors(dropErrors);
    setFiles(updated);
    onChange(updated);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop
  });

  useEffect(() => {
    // when value is cleared from outside, clear the files and error state too
    if (value && !value.length) {
      setFiles([]);
      setErrors([]);
    }
  }, [value]);

  const fieldCSS = cn('field images-field', error && 'error');
  const dropCSS = cn(
    'images-field__drop',
    isDragActive && 'images-field__drop--dragging',
    files.length && 'images-field__drop--dropped'
  );

  const renderPreview = (file, idx) => {
    // file.preview will be undefined on IE11
    const [fileName, ext] = file.info;
    const isImage = file.preview && imageExt.includes(ext && ext.toLowerCase());
    return (
      <Grid.Column key={idx}>
        <Card color="grey">
          <figure className={cn(!isImage && 'images-field__dummy')}>
            {isImage && <Image src={file.preview} alt={file.name} />}
            {!isImage && (
              <Fragment>
                <Svg src={docOutlineIcon} />
                <span>{ext}</span>
              </Fragment>
            )}
          </figure>
          <Icon
            link
            name="close"
            className="circle"
            onClick={e => {
              e.stopPropagation();
              // eslint-disable-next-line no-unused-vars
              const filtered = files.filter((f, i) => i !== idx);
              setFiles(filtered);
              onChange(filtered);
            }}
          >
            <span role="button" />
          </Icon>
          <Card.Content textAlign="left">
            <Card.Description className="text--wrap">
              {`${Formatters.truncateString(fileName, 50)}.${ext}`}
            </Card.Description>
          </Card.Content>
          <Card.Content textAlign="left" extra>
            {Formatters.imageSize(file.size)}
          </Card.Content>
        </Card>
      </Grid.Column>
    );
  };

  // querySelector does not work with dots(.) - for e2e
  const id = name && name.replace('.', '_');
  return (
    <StyledImagesField className={fieldCSS} data-testid="images-field">
      <label htmlFor={id}>{label}</label>
      <div data-qa={qa} {...getRootProps()} className={dropCSS}>
        <input id={id} {...getInputProps()} />
        {error && (
          <Label basic pointing color="red">
            {error}
          </Label>
        )}

        {files.length ? (
          <Grid className="images-field__preview">
            {files.map(renderPreview)}
            {files.length < 5 && (
              <Grid.Column className="images-field__drop__placeholder">
                <Icon name="plus" />
              </Grid.Column>
            )}
          </Grid>
        ) : (
          <Fragment>
            <Svg src={cameraIcon} />
            <p>
              <FormattedMessage
                id="form.drop_photos"
                defaultMessage="Drop jpg, png or pdf files here or {browse}"
                values={{
                  browse: <a>browse</a>
                }}
              />
            </p>
          </Fragment>
        )}
      </div>

      {errors.length > 0 && (
        <Label basic color="red">
          {errors.map((err, i) => (
            <p key={i}>{err}</p>
          ))}
        </Label>
      )}
    </StyledImagesField>
  );
};

ImagesField.propTypes = {
  className: PropTypes.string,
  error: PropTypes.node,
  label: PropTypes.node,
  value: PropTypes.arrayOf(PropTypes.object),
  limitExt: PropTypes.arrayOf(PropTypes.string)
};
export default ImagesField;



// WEBPACK FOOTER //
// ./src/components/FormComponents/ImagesField/ImagesField.js